// AuthGate is the single integration point that lifts Mini App startup into
// authenticated state. Responsibilities:
//   1. Telegram lifecycle: signal ready, expand the viewport.
//   2. Pull initDataRaw via @telegram-apps/sdk.
//   3. Wire the shared fetch-client's AuthBridge so every /api/* call gets
//      the JWT and survives a single 401 via refresh.
//   4. Drive the auth-store through its state machine on mount.
//   5. Render a one-line loading skeleton while pending, a recoverable error
//      screen on failure, the children once authed.
//
// Lives in `app/providers` because it composes layers above entities — only
// `app` is allowed to reach into entities + shared per the FSD layer graph.

import { useEffect, useRef, type ReactNode } from "react";

import {
  postAuthInitData,
  postAuthRefresh,
  useAuth,
  useAuthStore,
} from "@entities/user";
import { setAuthBridge } from "@shared/api";
import {
  expandMiniAppViewport,
  mountTelegramChrome,
  readLaunchParams,
  signalMiniAppReady,
} from "@shared/lib";
import { RetryNotice } from "@shared/ui";

interface AuthGateProps {
  children: ReactNode;
  /**
   * Test/storybook escape hatch — when provided, replaces both the launch-
   * param probe and the network call. Production code never sets this.
   */
  bootstrap?: {
    readLaunchParams?: typeof readLaunchParams;
    postAuthInitData?: typeof postAuthInitData;
    postAuthRefresh?: typeof postAuthRefresh;
  };
}

export function AuthGate({ children, bootstrap }: AuthGateProps) {
  const { status } = useAuth();
  // useRef so React Strict Mode's double-invoke of effects in dev doesn't
  // fire two parallel sign-in requests against the backend.
  const startedRef = useRef(false);
  const initDataRef = useRef<string | null>(null);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const readParams = bootstrap?.readLaunchParams ?? readLaunchParams;
    const postInit = bootstrap?.postAuthInitData ?? postAuthInitData;
    const postRefresh = bootstrap?.postAuthRefresh ?? postAuthRefresh;

    signalMiniAppReady();
    expandMiniAppViewport();
    // Wire Telegram theme + viewport CSS vars and the .dark class. Also
    // installs a prefers-color-scheme listener for dev preview outside
    // Telegram. AuthGate lives for the whole session, so the cleanup is
    // intentionally not scheduled — see the comment at the bottom of the
    // effect about not tearing the auth bridge down on Strict Mode
    // double-mounts; the same logic applies here.
    mountTelegramChrome();

    setAuthBridge({
      getToken: () => useAuthStore.getState().token,
      refresh: async () => {
        const initData = initDataRef.current;
        if (!initData) return false;
        const store = useAuthStore.getState();
        store.setAuthenticating();
        try {
          const response = await postRefresh(initData);
          store.setAuthenticated({
            token: response.token,
            expiresAt: response.expiresAt,
            user: response.user,
          });
          return true;
        } catch (err) {
          store.setError(err instanceof Error ? err.message : "Refresh failed");
          return false;
        }
      },
    });

    const launch = readParams();
    if (!launch) {
      useAuthStore
        .getState()
        .setError("Telegram initData unavailable — open via the bot.");
      return;
    }
    initDataRef.current = launch.initDataRaw;

    useAuthStore.getState().setAuthenticating();
    postInit(launch.initDataRaw)
      .then((response) => {
        useAuthStore.getState().setAuthenticated({
          token: response.token,
          expiresAt: response.expiresAt,
          user: response.user,
        });
      })
      .catch((err: unknown) => {
        useAuthStore
          .getState()
          .setError(err instanceof Error ? err.message : "Authentication failed");
      });
    // No cleanup: AuthGate sits at the root of the provider tree and lives
    // for the lifetime of the Mini App session. Tearing the bridge down on
    // a hot-reload re-render (or React Strict Mode's double-mount) would
    // race with in-flight /api/* requests and produce spurious 401s.
  }, [bootstrap]);

  if (status === "authenticated") {
    return <>{children}</>;
  }

  if (status === "error") {
    const message =
      useAuthStore.getState().error ?? "Authentication failed.";
    return <AuthErrorScreen message={message} onRetry={retry} />;
  }

  return <AuthLoadingScreen />;
}

function retry() {
  // Force a full re-attempt by resetting the store and reloading the route.
  // The mounted AuthGate effect re-fires because startedRef belongs to a
  // freshly-mounted component instance after window.location.reload().
  useAuthStore.getState().reset();
  if (typeof window !== "undefined") {
    window.location.reload();
  }
}

function AuthLoadingScreen() {
  // Skeleton mirrors the eventual page chrome (header bar + 4 cards) so the
  // hand-off to the real Overview screen does not shift layout. Per task
  // policy this uses skeletons — never spinners.
  return (
    <main
      role="status"
      aria-live="polite"
      aria-label="Signing you in"
      className="container flex min-h-screen flex-col gap-4 py-4"
    >
      <div className="flex items-center justify-between gap-3 pb-2">
        <div className="h-6 w-32 animate-pulse rounded-md bg-muted" />
        <div className="h-11 w-11 animate-pulse rounded-md bg-muted" />
      </div>
      <div className="flex justify-center">
        <div className="h-40 w-40 animate-pulse rounded-full bg-muted" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="h-24 animate-pulse rounded-md bg-muted" />
        <div className="h-24 animate-pulse rounded-md bg-muted" />
        <div className="h-24 animate-pulse rounded-md bg-muted" />
        <div className="h-24 animate-pulse rounded-md bg-muted" />
      </div>
    </main>
  );
}

interface AuthErrorScreenProps {
  message: string;
  onRetry: () => void;
}

function AuthErrorScreen({ message, onRetry }: AuthErrorScreenProps) {
  return (
    <main className="flex min-h-screen items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <RetryNotice
          title="Couldn't sign you in"
          body={message}
          retryLabel="Try again"
          onRetry={onRetry}
          variant="block"
        />
      </div>
    </main>
  );
}
